/**
 * „Auswahl bearbeiten": dieselben Felder für alle ausgewählten Einträge einer KIAN-Karte setzen —
 * Kunde, Status, Priorität, Kategorie, Bearbeiter, Fälligkeit. Leere Felder bleiben, wie sie sind.
 * Bei 40 Punkten aus einer Liste ist das der Unterschied zwischen einem Dialog und vierzig.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { useCustomerSearchSelect } from './customerSearchSelect';
import type { KianActionKind } from '../../../lib/api/domains/kianActions';
import { hasBulkChanges, kianAssigneeOptions, plural, type KianBulkChanges } from './kianActionUi';

interface Props {
  opened: boolean;
  onClose: () => void;
  kind: KianActionKind;
  /** Anzahl der ausgewählten, noch änderbaren Einträge */
  count: number;
  saving: boolean;
  onApply: (changes: KianBulkChanges) => void;
}

export function KianBulkEditModal({ opened, onClose, kind, count, saving, onApply }: Props) {
  const create = kind === 'TICKET_CREATE';
  const [changes, setChanges] = useState<KianBulkChanges>({});
  const customerSelect = useCustomerSearchSelect({ enabled: opened && create, selectedId: changes.customerId ?? null });

  useEffect(() => {
    if (opened) {
      setChanges({});
      customerSelect.reset();
    }
  }, [opened]);

  const metadata = useQuery({
    queryKey: ['weclapp-metadata'],
    queryFn: () => api.getWeclappMetadata(),
    staleTime: 5 * 60_000,
    enabled: opened,
  });
  const users = useQuery({
    queryKey: ['portal-technicians'],
    queryFn: () => api.getPortalTechnicians(),
    staleTime: 120_000,
    enabled: opened,
  });

  const set = <K extends keyof KianBulkChanges>(k: K, v: KianBulkChanges[K]) => setChanges((c) => ({ ...c, [k]: v }));

  const statusOptions = useMemo(
    () => (metadata.data?.statuses ?? []).map((s) => ({ value: String(s.id), label: s.name })),
    [metadata.data],
  );
  const priorityOptions = useMemo(
    () => (metadata.data?.priorities ?? []).map((p) => ({ value: String(p.id), label: p.name })),
    [metadata.data],
  );
  const categoryOptions = useMemo(
    () => (metadata.data?.categories ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    [metadata.data],
  );
  const userOptions = useMemo(
    () => [
      { value: 'self', label: 'Ich' },
      ...(create ? [] : [{ value: 'pool', label: 'Zurück in den Pool' }]),
      ...kianAssigneeOptions(users.data?.technicians),
    ],
    [users.data, create],
  );

  // Wie im Einzel-Editor: Bei Änderungen verlangt die Zuweisung an andere bzw. die Pool-Rückgabe
  // eine Begründung — der Server prüft sie beim Auflösen und beim Ausführen.
  const needsReason = !create && !!changes.assignee && changes.assignee !== 'self';

  return (
    <Modal opened={opened} onClose={onClose} title="Auswahl bearbeiten" size="lg" centered zIndex={5300}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Gilt für {plural(count, 'ausgewählten Eintrag', 'ausgewählte Einträge')}. Was du leer lässt, bleibt, wie es ist.
        </Text>

        {create ? (
          <Select
            label="Kunde"
            placeholder="Unverändert — Kundennamen oder -nummer eingeben…"
            data={customerSelect.data}
            value={changes.customerId ?? null}
            onChange={(v) => set('customerId', v)}
            searchable
            searchValue={customerSelect.searchValue}
            onSearchChange={customerSelect.onSearchChange}
            clearable
            nothingFoundMessage={customerSelect.nothingFoundMessage}
            comboboxProps={{ zIndex: 5400 }}
          />
        ) : null}

        <Group grow align="flex-start">
          <Select
            label="Status"
            placeholder="Unverändert"
            data={statusOptions}
            value={changes.statusId ?? null}
            onChange={(v) => set('statusId', v)}
            searchable
            clearable
            comboboxProps={{ zIndex: 5400 }}
          />
          <Select
            label="Priorität"
            placeholder="Unverändert"
            data={priorityOptions}
            value={changes.priorityId ?? null}
            onChange={(v) => set('priorityId', v)}
            clearable
            comboboxProps={{ zIndex: 5400 }}
          />
        </Group>
        <Group grow align="flex-start">
          <Select
            label="Kategorie"
            placeholder="Unverändert"
            data={categoryOptions}
            value={changes.categoryId ?? null}
            onChange={(v) => set('categoryId', v)}
            searchable
            clearable
            comboboxProps={{ zIndex: 5400 }}
          />
          <Select
            label="Bearbeiter"
            placeholder="Unverändert"
            data={userOptions}
            value={changes.assignee ?? null}
            onChange={(v) => set('assignee', v)}
            searchable
            clearable
            comboboxProps={{ zIndex: 5400 }}
          />
        </Group>
        {needsReason ? (
          <TextInput
            label="Begründung"
            description="Pflicht bei Pool-Rückgabe und bei Zuweisung an andere durch den Teamlead (mind. 10 Zeichen)"
            value={changes.assignReason ?? ''}
            onChange={(e) => {
              const v = e.currentTarget.value;
              set('assignReason', v || null);
            }}
          />
        ) : null}
        <TextInput
          label="Fällig am"
          type="date"
          value={changes.dueDate ?? ''}
          onChange={(e) => {
            const v = e.currentTarget.value;
            set('dueDate', v || null);
          }}
        />

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button loading={saving} disabled={!hasBulkChanges(changes) || count === 0} onClick={() => onApply(changes)}>
            Für {plural(count, 'Eintrag', 'Einträge')} übernehmen
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
